import axios from 'axios'
import { auth } from '../config/firebase'

// URL base del backend
const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://institucioneducativajgmm.edu.co/api'

// Crear instancia de axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor para agregar el token de Firebase en cada petición
apiClient.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser
    if (user) {
      const token = await user.getIdToken()
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar errores
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('No autorizado - Token inválido o expirado')
    }
    return Promise.reject(error)
  }
)

export default {
  // ============ AUTH ENDPOINTS ============
  
  // Verificar token y autorización
  async verifyAuth() {
    const response = await apiClient.get('/auth/verify')
    return response.data
  },

  // Obtener usuario actual
  async getCurrentUser() {
    const response = await apiClient.get('/auth/me')
    return response.data
  },

  // ============ USUARIOS ENDPOINTS ============
  
  // Crear usuario autorizado
  async createUser(userData) {
    const response = await apiClient.post('/usuarios', userData)
    return response.data
  },

  // Listar usuarios
  async getUsers(onlyActive = true) {
    const response = await apiClient.get('/usuarios', {
      params: { soloActivos: onlyActive }
    })
    return response.data
  },

  // Obtener usuario por ID
  async getUser(id) {
    const response = await apiClient.get(`/usuarios/${id}`)
    return response.data
  },

  // Actualizar usuario
  async updateUser(id, userData) {
    const response = await apiClient.put(`/usuarios/${id}`, userData)
    return response.data
  },

  // Activar/Desactivar usuario
  async activateUser(id) {
    const response = await apiClient.patch(`/usuarios/${id}/activar`)
    return response.data
  },

  async deactivateUser(id) {
    const response = await apiClient.patch(`/usuarios/${id}/desactivar`)
    return response.data
  },

  // ============ TIPOS DE DOCUMENTO ENDPOINTS ============
  
  // Crear tipo de documento
  async createDocumentType(typeData) {
    const response = await apiClient.post('/tipos-documento', typeData)
    return response.data
  },

  // Listar tipos de documento
  async getDocumentTypes(onlyActive = true) {
    const response = await apiClient.get('/tipos-documento', {
      params: { soloActivos: onlyActive }
    })
    return response.data
  },

  // Obtener tipo de documento por ID
  async getDocumentType(id) {
    const response = await apiClient.get(`/tipos-documento/${id}`)
    return response.data
  },

  // Obtener tipo de documento por código
  async getDocumentTypeByCode(code) {
    const response = await apiClient.get(`/tipos-documento/codigo/${code}`)
    return response.data
  },

  // Actualizar tipo de documento
  async updateDocumentType(id, typeData) {
    const response = await apiClient.put(`/tipos-documento/${id}`, typeData)
    return response.data
  },

  // ============ DOCUMENTOS ENDPOINTS ============
  
  // Subir documento
  async uploadDocument(file, tipoDocumentoId, comentario = '') {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('tipoDocumentoId', tipoDocumentoId)
    if (comentario) {
      formData.append('comentario', comentario)
    }

    const response = await apiClient.post('/documentos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  // Listar documentos activos
  async getDocuments() {
    const response = await apiClient.get('/documentos')
    return response.data
  },

  // Obtener documento por ID
  async getDocument(id) {
    const response = await apiClient.get(`/documentos/${id}`)
    return response.data
  },

  // Listar documentos por tipo
  async getDocumentsByType(tipoDocumentoId) {
    const response = await apiClient.get(`/documentos/tipo/${tipoDocumentoId}`)
    return response.data
  },

  // Obtener última versión de un tipo de documento
  async getLatestDocumentVersion(tipoDocumentoId) {
    const response = await apiClient.get(`/documentos/tipo/${tipoDocumentoId}/ultima-version`)
    return response.data
  },

  // Descargar documento
  getDocumentDownloadUrl(id) {
    return `${API_BASE_URL}/documentos/${id}/descargar`
  },

  // Eliminar documento
  async deleteDocument(id) {
    const response = await apiClient.delete(`/documentos/${id}`)
    return response.data
  },

  // Obtener documentos recientes
  async getRecentDocuments(limite = 10) {
    const response = await apiClient.get('/documentos/recientes', {
      params: { limite }
    })
    return response.data
  }
}
